import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, CheckCircle, Clock, Flag, HelpCircle, Lock, Send, ShieldCheck } from 'lucide-react';
import { useGeo } from '../context/GeoContext';
import { apiGet, apiPost, bootstrapBffSession } from '../lib/apiClient';
import { getRecaptchaToken } from '../lib/recaptcha';
import { loadAccessCodeState, rememberAccessCode, forgetAccessCode } from '../lib/accessCodeStorage';
import '../styles/SurveyForm.css';
import '../styles/SurveyDetailPage.css';
import { useAnonConsent } from '../context/AnonConsentContext';
import AccessCodeModal from '../components/AccessCodeModal';

const MAX_CODE_ATTEMPTS = 3;

const formatDateTimeDisplay = (value) => {
  if (!value) return '';
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return '';
  return dt.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
};

const isAnswerFilled = (value) => {
  if (Array.isArray(value)) return value.some((entry) => String(entry || '').trim() !== '');
  if (value === undefined || value === null) return false;
  return String(value).trim() !== '';
};

export default function SurveyDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { coords, refreshLocation } = useGeo();
  const { consented, loading: consentLoading } = useAnonConsent();

  const [survey, setSurvey] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [challengeQuestions, setChallengeQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [challengeAnswers, setChallengeAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [done, setDone] = useState(false);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [challengeBlocked, setChallengeBlocked] = useState(false);
  const [challengeNotice, setChallengeNotice] = useState('');
  const [reported, setReported] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [reportMode, setReportMode] = useState(false);
  const [reportText, setReportText] = useState('');
  const [codeModalOpen, setCodeModalOpen] = useState(false);
  const [codeError, setCodeError] = useState('');
  const [codeAttempts, setCodeAttempts] = useState(0);
  const [codeLocked, setCodeLocked] = useState(false);
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [accessCode, setAccessCode] = useState('');
  const [codeConfirmed, setCodeConfirmed] = useState(false);
  const [expired, setExpired] = useState(false);
  const [expiresAt, setExpiresAt] = useState(null);
  const surveyTitle = typeof survey?.title === 'string' ? survey.title.trim() : '';
  const surveyInstructions = typeof survey?.instructions === 'string' ? survey.instructions.trim() : '';

  const isMounted = useRef(true);
  const storageStateRef = useRef({ enabled: true });
  const shareTokenRef = useRef('');
  useEffect(() => () => { isMounted.current = false; }, []);

  const replaceCodeInUrl = useCallback((nextCode) => {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    const current = url.searchParams.get('code') || '';
    const normalizedNext = (nextCode || '').trim();
    if (normalizedNext) {
      if (current === normalizedNext) return;
      url.searchParams.set('code', normalizedNext);
    } else {
      if (!current) return;
      url.searchParams.delete('code');
    }
    const search = url.searchParams.toString();
    const nextHref = `${url.pathname}${search ? `?${search}` : ''}${url.hash}`;
    window.history.replaceState({}, '', nextHref);
  }, []);

  const fetchSurvey = useCallback(async ({ codeValue = '', countAttempt = false, skipLoading = false } = {}) => {
    isMounted.current = true;
    if (!skipLoading) setLoading(true);
    setLoadError(null);
    setSubmitError(null);
    try {
      await bootstrapBffSession();
      const params = new URLSearchParams();
      if (codeValue) params.set('code', codeValue);
      if (shareTokenRef.current) params.set('share_token', shareTokenRef.current);
      const suffix = params.toString() ? `?${params.toString()}` : '';
      const identifier = id || '';
      const numericIdentifier = /^\d+$/.test(identifier);
      const metaPath = numericIdentifier
        ? `/surveys/${identifier}${suffix}`
        : `/surveys/uuid/${identifier}${suffix}`;
      const meta = await apiGet(metaPath);
      const surveyPayload = meta?.survey || null;
      const requiresCodeFlag = Boolean(surveyPayload?.requires_code);
      if (surveyPayload) {
        setSurvey(surveyPayload);
        const expiresValue = surveyPayload.expiresAt || null;
        setExpiresAt(expiresValue);
        setExpired(Boolean(surveyPayload.expired));
      } else {
        setSurvey(null);
        setExpiresAt(null);
        setExpired(false);
      }

      if (requiresCodeFlag && !codeValue) {
        setQuestions([]);
        setAccessCode('');
        setCodeModalOpen(true);
        setCodeError('This survey requires an access code.');
        setCodeAttempts(0);
        setCodeLocked(false);
        if (storageStateRef.current.enabled) {
          const ok = forgetAccessCode(id);
          if (!ok) storageStateRef.current.enabled = false;
        }
        replaceCodeInUrl('');
        setCodeConfirmed(false);
        return false;
      }

      const followupId = surveyPayload?.id != null
        ? String(surveyPayload.id)
        : (numericIdentifier ? identifier : null);
      if (!followupId) {
        throw new Error('Survey metadata missing survey id');
      }
      const qRes = await apiGet(`/surveys/${followupId}/questions${suffix}`);
      if (!isMounted.current) {
        return false;
      }
      const allQuestions = Array.isArray(qRes.items) ? qRes.items : [];
      const challengeList = allQuestions.filter((q) => q.is_challenge || q.isChallenge);
      const regularList = allQuestions.filter((q) => !(q.is_challenge || q.isChallenge));
      setQuestions(regularList);
      setChallengeQuestions(challengeList);
      setAnswers({});
      setChallengeAnswers({});
      setChallengeNotice(String(qRes.challengeMessage || ''));
      setChallengeBlocked(Boolean(qRes.challengeBlocked));
      if (typeof qRes.expired === 'boolean') {
        setExpired(Boolean(qRes.expired));
      }
      if (qRes.expiresAt != null) {
        setExpiresAt(qRes.expiresAt);
      }
      setDone(false);
      setAlreadySubmitted(false);
      setCodeError('');
      setCodeAttempts(0);
      setCodeLocked(false);
      if (codeValue) {
        setAccessCode(codeValue);
        replaceCodeInUrl(codeValue);
        if (storageStateRef.current.enabled) {
          const ok = rememberAccessCode(id, codeValue);
          if (!ok) storageStateRef.current.enabled = false;
        }
      } else {
        if (storageStateRef.current.enabled) {
          const ok = forgetAccessCode(id);
          if (!ok) storageStateRef.current.enabled = false;
        }
        replaceCodeInUrl('');
      }
      if (requiresCodeFlag) {
        if (countAttempt) {
          setCodeConfirmed(true);
          setCodeModalOpen(false);
        } else {
          setCodeConfirmed(false);
          setCodeModalOpen(true);
        }
      } else {
        setCodeConfirmed(true);
        setCodeModalOpen(false);
      }
      return true;
    } catch (e) {
      if (!isMounted.current) return;
      if (e?.status === 403) {
        setSurvey(null);
        setExpiresAt(null);
        setExpired(false);
        setQuestions([]);
        setChallengeQuestions([]);
        setChallengeAnswers({});
        setChallengeNotice('');
        setChallengeBlocked(false);
        setCodeError(e?.msg || e?.message || 'Access code required for this survey.');
        if (countAttempt) {
          setCodeAttempts((prev) => {
            const next = prev + 1;
            if (next >= MAX_CODE_ATTEMPTS) {
              setCodeLocked(true);
              setCodeError('Too many incorrect attempts. Please contact the survey owner for assistance.');
            }
            return next;
          });
        } else {
          setCodeAttempts(0);
          setCodeLocked(false);
        }
        setCodeModalOpen(true);
        if (storageStateRef.current.enabled) {
          const ok = forgetAccessCode(id);
          if (!ok) storageStateRef.current.enabled = false;
        }
        replaceCodeInUrl('');
        setCodeConfirmed(false);
        return false;
      } else {
        setExpired(false);
        setExpiresAt(null);
        setLoadError(e?.msg || e?.message || 'Failed to load survey');
        return false;
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  }, [id, replaceCodeInUrl]);

  useEffect(() => {
    const urlParams = (typeof window !== 'undefined') ? new URLSearchParams(window.location.search) : null;
    const codeFromUrl = urlParams ? (urlParams.get('code') || '').trim() : '';
    const shareToken = urlParams ? (urlParams.get('share_token') || '').trim() : '';
    shareTokenRef.current = shareToken;
    const { enabled } = loadAccessCodeState();
    storageStateRef.current.enabled = enabled;
    setCodeConfirmed(false);
    if (codeFromUrl) {
      setAccessCode(codeFromUrl);
      fetchSurvey({ codeValue: codeFromUrl });
    } else {
      setAccessCode('');
      fetchSurvey();
    }
  }, [fetchSurvey, id]);

  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      const paramsCurrent = new URLSearchParams(window.location.search);
      const codeFromUrl = (paramsCurrent.get('code') || '').trim();
      shareTokenRef.current = (paramsCurrent.get('share_token') || '').trim();
      const candidate = codeFromUrl || accessCode;
      fetchSurvey({ codeValue: candidate, skipLoading: true });
    };
    const handlePageShow = (event) => {
      if (event.persisted) {
        handleVisibility();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('pageshow', handlePageShow);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [fetchSurvey, accessCode]);

  const handleCodeSubmit = useCallback(async (value) => {
    if (codeLocked) return false;
    const trimmed = (value || '').trim();
    if (!trimmed) {
      setCodeError('Please enter the access code.');
      return false;
    }
    setVerifyingCode(true);
    const ok = await fetchSurvey({ codeValue: trimmed, countAttempt: true, skipLoading: true });
    setVerifyingCode(false);
    if (ok) {
      setAccessCode(trimmed);
    }
    return ok;
  }, [codeLocked, fetchSurvey]);

  useEffect(() => {
    if (consented && submitError) {
      setSubmitError(null);
    }
  }, [consented, submitError]);

  const handleChange = (qid, value) => {
    if (!consented) return;
    setAnswers((prev) => ({ ...prev, [qid]: value }));
    if (submitError) setSubmitError(null);
  };

  const handleChallengeChange = (qid, value) => {
    if (!consented) return;
    setChallengeAnswers((prev) => ({ ...prev, [qid]: value }));
    if (submitError) setSubmitError(null);
  };

  const toggleMulti = (qid, opt, checked) => {
    if (!consented) return;
    setAnswers((prev) => {
      const current = Array.isArray(prev[qid]) ? prev[qid] : [];
      let next;
      if (checked) {
        next = current.includes(opt) ? current : [...current, opt];
      } else {
        next = current.filter((v) => v !== opt);
      }
      if (submitError) setSubmitError(null);
      return { ...prev, [qid]: next };
    });
  };

  const hasAnswers = useMemo(() => {
    return Object.values(answers).some(isAnswerFilled);
  }, [answers]);
  const challengeComplete = useMemo(() => {
    if (challengeQuestions.length === 0) return true;
    return challengeQuestions.every((q) => {
      return isAnswerFilled(challengeAnswers[q.id]);
    });
  }, [challengeAnswers, challengeQuestions]);
  const expiresAtLabel = useMemo(() => formatDateTimeDisplay(expiresAt), [expiresAt]);
  const answeredQuestionCount = useMemo(() => {
    return questions.reduce((count, q) => count + (isAnswerFilled(answers[q.id]) ? 1 : 0), 0);
  }, [answers, questions]);
  const regularQuestionCount = questions.length;
  const progressPercent = regularQuestionCount > 0
    ? Math.round((answeredQuestionCount / regularQuestionCount) * 100)
    : 0;
  const accessLabel = survey?.requires_code
    ? (codeConfirmed ? 'Access verified' : 'Code required')
    : 'Public survey';
  const responseStatusLabel = expired
    ? 'Closed'
    : (expiresAtLabel ? `Closes ${expiresAtLabel}` : 'Open');
  const canSubmit =
    consented &&
    hasAnswers &&
    challengeComplete &&
    (!survey?.requires_code || codeConfirmed) &&
    !challengeBlocked &&
    !expired;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!hasAnswers) return;
    if (!consented) {
      setSubmitError('Enable anonymous ID participation to submit responses.');
      return;
    }
    if (challengeBlocked) {
      setSubmitError(challengeNotice || 'You are blocked from responding to this survey.');
      return;
    }
    if (!challengeComplete) {
      setSubmitError('Please answer the challenge questions before submitting.');
      return;
    }
    if (expired) {
      setSubmitError('This survey is no longer accepting responses.');
      return;
    }

    setSaving(true);
    setSubmitError(null);

    // Ensure BFF session (sets anon:<stable_uid> if not logged in)
    await bootstrapBffSession();

    // Refresh location and include lat/lng for proximity enforcement
    let lat = coords?.lat ?? null;
    let lng = coords?.lng ?? null;
    const r = await refreshLocation();
    if (r?.ok && r.coords) {
      lat = r.coords.lat;
      lng = r.coords.lng;
    }

    try {
      const payload = { answers, lat, lng };
      if (challengeQuestions.length > 0) {
        payload.challenge_answers = challengeAnswers;
      }
      if (accessCode) payload.code = accessCode;

      let recaptchaToken = null;
      try {
        recaptchaToken = await getRecaptchaToken('survey_response');
      } catch (err) {
        setSubmitError(err?.message || 'reCAPTCHA verification failed. Please retry.');
        return;
      }

      const options = recaptchaToken ? { headers: { 'X-Recaptcha-Token': recaptchaToken } } : undefined;
      const resolvedSurveyId = survey?.id;
      if (!resolvedSurveyId) {
        throw new Error('Survey metadata unavailable');
      }
      const resp = await apiPost(`/surveys/${resolvedSurveyId}/responses`, payload, options);
      setAlreadySubmitted(Boolean(resp?.duplicate));
      setDone(true);
    } catch (e) {
      if (e?.status === 410) {
        const closure = e?.msg || e?.message || 'This survey is no longer accepting responses.';
        const expiresValue =
          e?.expiresAt ||
          (e?.data && e.data.expiresAt) ||
            expiresAt;
        if (expiresValue) {
          setExpiresAt(expiresValue);
        }
        setExpired(true);
        setSubmitError(closure);
        return;
      }
      const errMsg = e?.msg || e?.message || 'Failed to submit survey response';
      if (e?.code === 'CHALLENGE_FAILED' || e?.code === 'CHALLENGE_BLOCKED' || e?.status === 423) {
        setChallengeBlocked(true);
        setChallengeNotice(errMsg);
      }
      setSubmitError(errMsg);
    } finally {
      setSaving(false);
    }
  };

  const needsConfirmation = survey?.requires_code && !codeConfirmed && questions.length > 0 && !expired;
  const codeModal = (
    <AccessCodeModal
      open={codeModalOpen}
      busy={verifyingCode}
      locked={codeLocked}
      attempts={codeAttempts}
      maxAttempts={MAX_CODE_ATTEMPTS}
      error={codeError}
      description={needsConfirmation
        ? 'Please re-enter the access code before submitting responses.'
        : 'This survey requires an access code. Enter it below to continue.'}
      onSubmit={handleCodeSubmit}
      onCancel={!codeLocked ? () => setCodeModalOpen(false) : undefined}
    />
  );

  if (loading) {
    return (
      <>
        {codeModal}
        <main className="survey-detail-page">
          <section className="survey-detail-card survey-detail-card--state">Loading survey...</section>
        </main>
      </>
    );
  }
  if (loadError) {
    return (
      <>
        {codeModal}
        <main className="survey-detail-page">
          <section className="survey-detail-card survey-detail-card--state survey-detail-card--error">
            <AlertTriangle size={18} aria-hidden="true" />
            <span>Error: {loadError}</span>
          </section>
        </main>
      </>
    );
  }
  if (!survey) {
    return (
      <>
        {codeModal}
        <main className="survey-detail-page">
          <section className="survey-detail-card survey-detail-card--state">Survey not found.</section>
        </main>
      </>
    );
  }

  if (done) {
    return (
      <>
        {codeModal}
        <main className="survey-detail-page">
          <section className="survey-detail-card survey-complete-card">
            <div className="survey-complete-icon">
              <CheckCircle size={28} aria-hidden="true" />
            </div>
            <h1>{alreadySubmitted ? 'Response already submitted' : 'Response submitted'}</h1>
            <p>
              {alreadySubmitted
                ? 'You can still review the survey and discussion.'
                : 'Thanks for sharing your feedback.'}
            </p>
            <button className="survey-btn survey-btn--secondary" onClick={() => navigate(-1)}>
              <ArrowLeft size={16} aria-hidden="true" />
              Back
            </button>
          </section>
        </main>
      </>
    );
  }

  return (
    <>
      {codeModal}
      <main className="survey-detail-page">
        <section className="survey-detail-card survey-detail-shell">
          <header className="survey-detail-hero">
            <div className="survey-detail-kicker">
              <span>Survey</span>
              <a href="/guide" className="survey-guide-link">
                <HelpCircle size={14} aria-hidden="true" />
                User Guide
              </a>
            </div>

            <div className="survey-detail-title-row">
              <div>
                <h1>{surveyTitle || 'Untitled survey'}</h1>
                {surveyInstructions && (
                  <p className="survey-detail-instructions">{surveyInstructions}</p>
                )}
              </div>
            </div>

            <div className="survey-status-row" aria-label="Survey status">
              <span className={`survey-status-chip ${expired ? 'survey-status-chip--closed' : 'survey-status-chip--open'}`}>
                <Clock size={14} aria-hidden="true" />
                {responseStatusLabel}
              </span>
              <span className={`survey-status-chip ${survey?.requires_code ? 'survey-status-chip--private' : ''}`}>
                <Lock size={14} aria-hidden="true" />
                {accessLabel}
              </span>
              <span className="survey-status-chip">
                {answeredQuestionCount}/{regularQuestionCount} answered
              </span>
            </div>

            <div className="survey-progress" aria-label={`${progressPercent}% complete`}>
              <div className="survey-progress__meta">
                <span>Progress</span>
                <strong>{progressPercent}%</strong>
              </div>
              <div className="survey-progress__track">
                <div className="survey-progress__bar" style={{ width: `${progressPercent}%` }} />
              </div>
            </div>
          </header>

        {reported && (
          <div className="survey-alert survey-alert--danger">
            <Flag size={16} aria-hidden="true" />
            Thank you for the report. Once this survey exceeds the removal threshold of unique reports from different users, it will be removed.
          </div>
        )}

        <form onSubmit={onSubmit} className="survey-detail-form">
          {expired && (
            <div className="survey-alert survey-alert--warning">
              <Clock size={16} aria-hidden="true" />
              {expiresAtLabel
                ? `Responses closed on ${expiresAtLabel}.`
                : 'This survey is no longer accepting responses.'}
            </div>
          )}
          {!consented && !consentLoading && (
            <div className="survey-alert survey-alert--info">
              <Lock size={16} aria-hidden="true" />
              <span>
                Read-only mode: enable anonymous ID participation on the <a href="/privacy/anonymous-id">ID Policy</a> page to unlock survey questions and submit responses.
              </span>
            </div>
          )}

          {challengeQuestions.length > 0 && (
            <section className="survey-section survey-section--challenge">
              <div className="survey-section__header">
                <div className="survey-section__icon survey-section__icon--challenge">
                  <ShieldCheck size={18} aria-hidden="true" />
                </div>
                <div>
                  <h2>Verification</h2>
                  <p>Answer these before submitting. They help verify responder authenticity.</p>
                </div>
              </div>
              {challengeNotice && (
                <div className={`survey-alert ${challengeBlocked ? 'survey-alert--danger' : 'survey-alert--info'}`}>
                  <AlertTriangle size={16} aria-hidden="true" />
                  {challengeNotice}
                </div>
              )}
              <div className="survey-question-stack">
                {challengeQuestions.map((q, index) => (
                  <div className="survey-question-card survey-question-card--challenge" key={`challenge-${q.id}`}>
                    <div className="survey-question-card__meta">Challenge {index + 1}</div>
                    <label className="survey-question-text" htmlFor={`challenge-${q.id}`}>
                      {q.question_text || q.questionText}
                    </label>
                    <textarea
                      id={`challenge-${q.id}`}
                      className="survey-free-text"
                      rows={3}
                      placeholder="Type your answer"
                      value={challengeAnswers[q.id] || ''}
                      onChange={(e) => handleChallengeChange(q.id, e.target.value)}
                      disabled={!consented || challengeBlocked || saving || expired}
                    />
                  </div>
                ))}
              </div>
            </section>
          )}

          <section className="survey-section">
            <div className="survey-section__header">
              <div className="survey-section__icon">{regularQuestionCount}</div>
              <div>
                <h2>Questions</h2>
                <p>Choose the answers that best match your experience.</p>
              </div>
            </div>

            <div className="survey-question-stack">
              {questions.map((q, index) => {
                const questionText = q.question_text || q.questionText;
                const questionType = q.question_type || q.questionType;
                const answered = isAnswerFilled(answers[q.id]);
                return (
                  <div className={`survey-question-card ${answered ? 'survey-question-card--answered' : ''}`} key={q.id}>
                    <div className="survey-question-card__meta">
                      <span>Question {index + 1}</span>
                      {answered && <span className="survey-question-card__answered">Answered</span>}
                    </div>
                    <div className="survey-question-text">{questionText}</div>

                    {(questionType === 'multiple_choice') && Array.isArray(q.options) && (
                      <div className="survey-options">
                        {q.options.map((opt, idx) => {
                          const isMulti = !!(q.allow_multiple || q.allowMultiple);
                          const selected = isMulti
                            ? Array.isArray(answers[q.id]) && answers[q.id].includes(opt)
                            : answers[q.id] === opt;
                          return (
                            <label key={idx} className={`survey-option-tile ${selected ? 'survey-option-tile--selected' : ''}`}>
                              {isMulti ? (
                                <input
                                  type="checkbox"
                                  name={`q-${q.id}`}
                                  value={opt}
                                  checked={selected}
                                  onChange={(e) => toggleMulti(q.id, opt, e.target.checked)}
                                  disabled={!consented || challengeBlocked || saving || expired}
                                />
                              ) : (
                                <input
                                  type="radio"
                                  name={`q-${q.id}`}
                                  value={opt}
                                  checked={selected}
                                  onChange={(e) => handleChange(q.id, e.target.value)}
                                  disabled={!consented || challengeBlocked || saving || expired}
                                />
                              )}
                              <span className="survey-option-tile__indicator" aria-hidden="true" />
                              <span>{opt}</span>
                            </label>
                          );
                        })}
                      </div>
                    )}

                    {questionType === 'free_text' && (
                      <textarea
                        className="survey-free-text"
                        rows={3}
                        placeholder="Type your answer"
                        value={answers[q.id] || ''}
                        onChange={(e) => handleChange(q.id, e.target.value)}
                        disabled={!consented || challengeBlocked || saving || expired}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </section>

          <div className="survey-action-bar">
            <div className="survey-action-bar__summary">
              <strong>{answeredQuestionCount}/{regularQuestionCount}</strong>
              <span>answered</span>
            </div>
            <div className="survey-action-bar__buttons">
              <button className="survey-btn survey-btn--primary" type="submit" disabled={saving || !canSubmit}>
                <Send size={16} aria-hidden="true" />
                {expired
                  ? 'Responses closed'
                  : saving
                    ? 'Submitting...'
                    : consented
                      ? 'Submit Answers'
                      : 'Enable participation'}
              </button>
              {survey?.requires_code && !codeConfirmed && (
                <button
                  type="button"
                  className="survey-btn survey-btn--secondary"
                  onClick={() => setCodeModalOpen(true)}
                  disabled={saving}
                >
                  <Lock size={16} aria-hidden="true" />
                  Enter Code
                </button>
              )}
              {!reportMode && (
                <button
                  type="button"
                  className="survey-btn survey-btn--ghost"
                  onClick={() => setReportMode(true)}
                  aria-label="Report survey"
                  title="Report survey for removal"
                >
                  <Flag size={16} aria-hidden="true" />
                  Report
                </button>
              )}
            </div>
          </div>

          {reportMode && (
            <section className="survey-report-panel">
              <div className="survey-section__header">
                <div className="survey-section__icon survey-section__icon--danger">
                  <Flag size={18} aria-hidden="true" />
                </div>
                <div>
                  <h2>Report this survey</h2>
                  <p>Provide a brief reason and avoid sensitive or personal data.</p>
                </div>
              </div>
              <textarea
                className="survey-free-text"
                rows={4}
                placeholder="Describe why this survey should be removed (e.g., spam, inappropriate content, personal data)"
                value={reportText}
                onChange={(e) => setReportText(e.target.value)}
              />
              <div className="survey-report-panel__actions">
                <button
                  type="button"
                  className="survey-btn survey-btn--danger"
                  disabled={reporting || reportText.trim().length < 3}
                  onClick={async () => {
                    try {
                      setReporting(true);
                      await bootstrapBffSession();
                      const body = { reason: reportText.trim() };
                      if (accessCode) body.code = accessCode;
                      let recaptchaToken = null;
                      try {
                        recaptchaToken = await getRecaptchaToken('survey_report');
                      } catch (err) {
                        alert(err?.message || 'reCAPTCHA verification failed. Please retry.');
                        return;
                      }
                      const targetSurveyId = survey?.id;
                      if (!targetSurveyId) throw new Error('Survey metadata unavailable');
                      await apiPost(`/surveys/${targetSurveyId}/report`, body, recaptchaToken ? { headers: { 'X-Recaptcha-Token': recaptchaToken } } : undefined);
                      setReported(true);
                      setReportMode(false);
                    } catch (e) {
                      alert(e?.msg || e?.message || 'Failed to report survey');
                    } finally {
                      setReporting(false);
                    }
                  }}
                >
                  {reporting ? 'Reporting...' : 'Submit Report'}
                </button>
                <button type="button" className="survey-btn survey-btn--secondary" onClick={() => { setReportMode(false); setReportText(''); }}>
                  Cancel
                </button>
              </div>
            </section>
          )}

          {saving && (
            <div
              aria-label="Submitting responses, please wait"
              role="status"
              className="progress-bar survey-submit-progress"
            >
              <div className="progress-bar-inner" />
            </div>
          )}
          {submitError && (
            <div className="survey-alert survey-alert--danger">
              <AlertTriangle size={16} aria-hidden="true" />
              {submitError}
            </div>
          )}
          {survey?.requires_code && !codeConfirmed && !codeModalOpen && (
            <div className="survey-alert survey-alert--info">
              <Lock size={16} aria-hidden="true" />
              Enter the access code before submitting your responses.
            </div>
          )}
        </form>
        </section>
      </main>
    </>
  );
}
